import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { callApi } from '../../utils/callApi';
import { ApiEvents } from '../../utils/SocketEvents';
import {
  ChekDoneTest,
  CheckDoneVote,
  GetDefaultLink,
  InteractiveActions,
  SendTest,
  SendVote,
  SendVoteGroup,
  ShowInteractive,
  TypeDoneTestR,
  TypeDoneVoteR,
  TypeSendTestR,
  TypeSendVoteR,
  TypeShowInteractiveR,
  CheckDoneVoteRating,
  SendVoteRating,
  SendVoteRatingGroup,
  TypeSendVoteRatingR,
  CheckDoneVoteRatingGroup,
  TypeDoneVoteGroupR,
  CheckDoneVoteGroup,
} from './actions';
import ActionTypes, {
  RatingType,
  TDoneVoteGroup,
  TDoneVoteRating,
  TDoneVoteRatingGroup,
  TInteractive,
  TInteractiveTest,
  TInteractiveVote,
  TLinks,
  TTestResult,
  TVoteRating,
  TVoteResult,
  TVotesGroup,
} from './types';
import { TypeInteractive } from '../../utils/TypeInteractive';
import { sendStat } from '../../hooks/sendStat';
import { interactiveType } from '../../utils/InteractiveType';
import { TYPE_EVENT_VOTE } from '../../utils/consts';

function* showInteractiveWorker(action: ReturnType<typeof ShowInteractive.request>): Generator {
  const { data, callBack } = action.payload as TypeShowInteractiveR;

  let success = true;
  try {
    const resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.showInteractive,
    })) as TInteractive;
    if (resp.interType === interactiveType.test) {
      yield put(InteractiveActions.setTest(resp.interactive as TInteractiveTest));
      yield put(InteractiveActions.setInteractive(TypeInteractive.test));
    }

    if (resp.interType === interactiveType.vote) {
      yield put(InteractiveActions.setVote(resp.interactive as TInteractiveVote));
      yield put(InteractiveActions.setInteractive(TypeInteractive.vote));
    }

    if (resp.interType === interactiveType.link) {
      yield put(InteractiveActions.setLink(resp.interactive as TLinks));
      yield put(InteractiveActions.setInteractive(TypeInteractive.link));
    }

    if (resp.interType === interactiveType.testResult) {
      yield put(InteractiveActions.setTestResult(resp.interactive as TTestResult));
      yield put(InteractiveActions.setInteractive(TypeInteractive.testResult));
    }

    if (resp.interType === interactiveType.voteResult) {
      yield put(InteractiveActions.setVoteResult(resp.interactive as TVoteResult));
      yield put(InteractiveActions.setInteractive(TypeInteractive.voteResult));
    }

    if (resp.interType === interactiveType.votesGroup) {
      yield put(InteractiveActions.setVotesGroup(resp.interactive as TVotesGroup));
      yield put(InteractiveActions.setInteractive(TypeInteractive.votesGroup));
    }

    if (resp.interType === interactiveType.voteRating) {
      yield put(InteractiveActions.setVoteRating(resp.interactive as TVoteRating));
      yield put(InteractiveActions.setInteractive(TypeInteractive.voteRating));
    }

    if (resp.interType === interactiveType.voteRatingResult) {
      yield put(InteractiveActions.setVoteRatingResult(resp.interactive as TVoteRating));
      yield put(InteractiveActions.setInteractive(TypeInteractive.voteRatingResult));
    }

    if (resp.interType === interactiveType.voteRatingGroup) {
      yield put(InteractiveActions.setVotesRatingGroup(resp.interactive as TVotesGroup));
      yield put(InteractiveActions.setInteractive(TypeInteractive.votesRatingGroup));
    }

    if (resp.interType === interactiveType.rating) {
      yield put(InteractiveActions.setRating(resp.interactive as RatingType));
      yield put(InteractiveActions.setInteractive(TypeInteractive.rating));
    }

    yield put(ShowInteractive.success());
  } catch (e) {
    success = false;
    yield put(ShowInteractive.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* sendTestWorker(action: ReturnType<typeof SendTest.request>): Generator {
  const { data, callBack } = action.payload as TypeSendTestR;

  let success = true;
  try {
    const resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.readyTest,
    })) as { points: number };
    sendStat(TYPE_EVENT_VOTE);
    yield put(SendTest.success({ ...resp, done: 1 }));
  } catch (e) {
    success = false;
    yield put(SendTest.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* sendVoteWorker(action: ReturnType<typeof SendVote.request>): Generator {
  const { data, callBack } = action.payload as TypeSendVoteR;

  let success = true;
  try {
    yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.readyVote,
    });
    sendStat(TYPE_EVENT_VOTE);
    yield put(SendVote.success({ done: 1 }));
  } catch (e) {
    success = false;
    yield put(SendVote.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* sendVoteRatingWorker(action: ReturnType<typeof SendVoteRating.request>): Generator {
  const { data, callBack } = action.payload as TypeSendVoteR;

  let success = true;
  try {
    const resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.readyVoteRating,
    })) as TDoneVoteRating;
    sendStat(TYPE_EVENT_VOTE);
    yield put(SendVoteRating.success(resp));
  } catch (e) {
    success = false;
    yield put(SendVoteRating.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* sendVoteGroupWorker(action: ReturnType<typeof SendVoteGroup.request>): Generator {
  const { data, callBack } = action.payload as TypeSendVoteR;

  let success = true;
  try {
    yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.readyVoteGroup,
    });
    sendStat(TYPE_EVENT_VOTE);
    yield put(SendVoteGroup.success());
  } catch (e) {
    success = false;
    yield put(SendVoteGroup.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* sendVoteRatingGroupWorker(action: ReturnType<typeof SendVoteRatingGroup.request>): Generator {
  const { data, callBack } = action.payload as TypeSendVoteRatingR;
  let resp: any;
  let success = true;
  try {
    resp = yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.readyVoteRatingGroup,
    });
    sendStat(TYPE_EVENT_VOTE);
    yield put(SendVoteRatingGroup.success());
  } catch (e) {
    success = false;
    yield put(SendVoteRatingGroup.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success, resp);
  }
}

function* checkDoneTestWorker(action: ReturnType<typeof ChekDoneTest.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneTestR;

  let success = true;
  let resp;
  try {
    resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.passedTest,
    })) as { done: 0 | 1; points: number };
    yield put(ChekDoneTest.success(resp));
  } catch (e) {
    success = false;
    yield put(ChekDoneTest.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* checkDoneVoteWorker(action: ReturnType<typeof CheckDoneVote.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneVoteR;

  let success = true;
  let resp;
  try {
    resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.passedVote,
    })) as { done: 0 | 1 };
    yield put(CheckDoneVote.success(resp));
  } catch (e) {
    success = false;
    yield put(CheckDoneVote.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success, resp);
  }
}

function* checkDoneVoteGroupWorker(action: ReturnType<typeof CheckDoneVoteGroup.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneVoteGroupR;

  let success = true;
  let resp;
  try {
    resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.passedVoteGroup,
    })) as TDoneVoteGroup;
    yield put(CheckDoneVoteGroup.success(resp));
  } catch (e) {
    success = false;
    yield put(CheckDoneVoteGroup.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success, resp);
  }
}

function* checkDoneVoteRatingWorker(action: ReturnType<typeof CheckDoneVoteRating.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneVoteR;

  let success = true;
  let resp;
  try {
    resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.passedVoteRating,
    })) as TDoneVoteRating;
    yield put(CheckDoneVoteRating.success(resp));
  } catch (e) {
    success = false;
    yield put(CheckDoneVoteRating.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success, resp);
  }
}

function* checkDoneVoteRatingGroupWorker(action: ReturnType<typeof CheckDoneVoteRatingGroup.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneVoteGroupR;

  let success = true;
  let resp;
  try {
    resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.passedVoteRatingGroup,
    })) as TDoneVoteRatingGroup;
    yield put(CheckDoneVoteRatingGroup.success(resp));
  } catch (e) {
    success = false;
    yield put(CheckDoneVoteRatingGroup.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success, resp);
  }
}

function* getDefaultLinkWorker(action: ReturnType<typeof GetDefaultLink.request>): Generator {
  const { data, callBack } = action.payload as TypeDoneVoteR;

  let success = true;
  try {
    const resp = (yield call(callApi, {
      method: 'post',
      data,
      path: ApiEvents.defaultLink,
    })) as TLinks;
    yield put(GetDefaultLink.success(resp));
  } catch (e) {
    success = false;
    yield put(GetDefaultLink.error(e as string));
  } finally {
    if (callBack) yield call(callBack, success);
  }
}

function* watchInteractiveFetchRequest() {
  yield takeEvery(ActionTypes.SHOW_INTERACTIVE_R, showInteractiveWorker);
  yield takeEvery(ActionTypes.SEND_TEST_R, sendTestWorker);
  yield takeEvery(ActionTypes.SEND_VOTE_R, sendVoteWorker);
  yield takeEvery(ActionTypes.SEND_VOTE_RATING_R, sendVoteRatingWorker);
  yield takeEvery(ActionTypes.SEND_VOTE_GROUP_R, sendVoteGroupWorker);
  yield takeEvery(ActionTypes.SEND_VOTE_RATING_GROUP_R, sendVoteRatingGroupWorker);
  yield takeEvery(ActionTypes.CHECK_DONE_TEST_R, checkDoneTestWorker);
  yield takeEvery(ActionTypes.CHECK_DONE_VOTE_R, checkDoneVoteWorker);
  yield takeEvery(ActionTypes.CHECK_DONE_VOTE_GROUP_R, checkDoneVoteGroupWorker);
  yield takeEvery(ActionTypes.CHECK_DONE_VOTE_RATING_R, checkDoneVoteRatingWorker);
  yield takeEvery(ActionTypes.CHECK_DONE_VOTE_RATING_GROUP_R, checkDoneVoteRatingGroupWorker);
  yield takeEvery(ActionTypes.GET_DEFAULT_LINK_R, getDefaultLinkWorker);
}

export default function* interactiveSaga() {
  yield all([fork(watchInteractiveFetchRequest)]);
}
